// src/features/insights/components/InsightTable.tsx
//
// Reusable "Component Island" that renders one side of the Momentum
// Insights dashboard — either the bullish (buy) or bearish (sell)
// leaderboard — based on the `theme` prop. Kept presentational and DRY so
// the page just drops in two instances with mirrored theming.
//
// Clicking a row teleports (client-side, zero network) to the matching
// deep-dive Info page. The breakout universe is equity+etf only, but we map
// every known asset_class defensively so the routing never dead-ends.

import { useNavigate } from 'react-router-dom';

import { formatMoney } from '../../../shared/utils/currency';
import type { BreakoutAlertDTO } from '../api';

interface InsightTableProps {
    title: string;
    items: BreakoutAlertDTO[];
    theme: 'buy' | 'sell';
}

// Canonical Info-page routes (mirrors App.tsx). Every deep-dive page reads
// its ticker from the `?symbol=` query param.
const routeForAsset = (asset: BreakoutAlertDTO): string => {
    const symbol = encodeURIComponent(asset.symbol);
    switch (asset.asset_class.toLowerCase()) {
        case 'etf':
            return `/dashboard/etf-info-page?symbol=${symbol}`;
        case 'reit':
            return `/dashboard/reit-info-page?symbol=${symbol}`;
        case 'crypto':
            return `/dashboard/crypto-info-page?symbol=${symbol}`;
        default:
            // 'equity' (and any unexpected class) lands on the stock page.
            return `/dashboard/stock-info-page?symbol=${symbol}`;
    }
};

export const InsightTable = ({ title, items, theme }: InsightTableProps) => {
    const navigate = useNavigate();
    const isBuy = theme === 'buy';

    // Premium institutional theming — emerald for longs, rose for shorts.
    const bgHeader = isBuy
        ? 'bg-emerald-50 dark:bg-emerald-900/10'
        : 'bg-rose-50 dark:bg-rose-900/10';
    const textColor = isBuy
        ? 'text-emerald-700 dark:text-emerald-400'
        : 'text-rose-700 dark:text-rose-400';
    const borderColor = isBuy
        ? 'border-emerald-200 dark:border-emerald-800'
        : 'border-rose-200 dark:border-rose-800';
    const hoverBorder = isBuy ? 'hover:border-l-emerald-500' : 'hover:border-l-rose-500';
    const barColor = isBuy
        ? 'bg-emerald-500 dark:bg-emerald-400'
        : 'bg-rose-500 dark:bg-rose-400';
    const dotColor = isBuy ? 'bg-emerald-500' : 'bg-rose-500';

    // Scale the conviction bars against the strongest surge in THIS list so
    // the #1 setup always fills the bar and the rest read relative to it.
    const maxSurge =
        items.length > 0
            ? Math.max(...items.map((i) => i.volume_surge_multiplier || 1.0))
            : 1.0;

    // Same resolution chain as the stock info page (explicit listing currency,
    // then exchange, then symbol suffix), so e.g. SAP.DE renders in EUR and an
    // LSE pence quote renders as GBX.
    const formatCurrency = (asset: BreakoutAlertDTO, val?: number | null) =>
        formatMoney(val, {
            explicitCurrency: asset.currency,
            exchange: asset.exchange,
            symbol: asset.symbol,
        });

    return (
        <div
            className={`flex flex-col h-full rounded-xl border ${borderColor} bg-white dark:bg-slate-900 shadow-sm overflow-hidden`}
        >
            {/* Header */}
            <div
                className={`px-5 py-4 border-b ${borderColor} ${bgHeader} flex items-center justify-between`}
            >
                <h2
                    className={`text-lg font-black tracking-tight ${textColor} flex items-center gap-2`}
                >
                    {isBuy ? 'LONG' : 'SHORT'} · {title}
                </h2>
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest bg-white dark:bg-slate-800 px-2 py-1 rounded border border-slate-200 dark:border-slate-700 shadow-sm">
                    Ranked by Conviction
                </span>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-x-auto">
                {items.length === 0 ? (
                    <div className="h-48 flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm p-6 text-center">
                        No extreme {theme.toUpperCase()} setups detected today.
                    </div>
                ) : (
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-200 dark:border-slate-700">
                            <tr>
                                <th className="px-4 py-3 text-center w-8">#</th>
                                <th className="px-4 py-3">Asset</th>
                                <th className="px-4 py-3 text-right">Close</th>
                                <th className="px-4 py-3 text-right">
                                    {isBuy ? 'Resistance' : 'Support'}
                                </th>
                                <th className="px-4 py-3 w-28">Vol Surge</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {items.map((asset, idx) => {
                                const surge = asset.volume_surge_multiplier || 0;
                                const widthPct = Math.min(
                                    100,
                                    (surge / maxSurge) * 100,
                                );
                                const isStrong = asset.signal.includes('STRONG');

                                return (
                                    <tr
                                        key={asset.symbol}
                                        onClick={() => navigate(routeForAsset(asset))}
                                        className={`group cursor-pointer bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-all border-l-4 border-transparent ${hoverBorder}`}
                                    >
                                        <td className="px-4 py-4 text-xs font-bold text-slate-400 dark:text-slate-500 text-center">
                                            {idx + 1}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <a
                                                        href={routeForAsset(asset)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className="font-bold text-blue-600 dark:text-blue-400 hover:underline"
                                                        title={asset.name ?? undefined}
                                                    >
                                                        {asset.symbol}
                                                    </a>
                                                    {isStrong && (
                                                        <span
                                                            className={`w-1.5 h-1.5 rounded-full ${dotColor} animate-pulse`}
                                                            title="Strong Conviction"
                                                        />
                                                    )}
                                                </div>
                                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                                                    {asset.region && asset.exchange
                                                        ? `${asset.region} · ${asset.exchange}`
                                                        : asset.asset_class}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-right font-mono font-medium text-slate-900 dark:text-white">
                                            {formatCurrency(asset, asset.close_price)}
                                        </td>
                                        <td className="px-4 py-4 text-right font-mono text-slate-500 dark:text-slate-400">
                                            {isBuy
                                                ? formatCurrency(asset, asset.prev_20d_high)
                                                : formatCurrency(asset, asset.prev_20d_low)}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex flex-col gap-1.5">
                                                <span
                                                    className={`font-mono text-xs font-bold ${textColor}`}
                                                >
                                                    {surge > 0
                                                        ? `${surge.toFixed(1)}x`
                                                        : '—'}
                                                </span>
                                                {/* Visual conviction bar */}
                                                <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${barColor}`}
                                                        style={{ width: `${widthPct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default InsightTable;
